// ---------------------------------
// client/src/App.jsx
// Main routing layout with protected routes
// ---------------------------------
import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import jwt_decode from 'jwt-decode';

import Header from './components/Header';
import Home from './pages/Home';
import Login from './pages/Login';
import Signup from './pages/Signup';
import Dashboard from './pages/Dashboard';
import JobDetails from './pages/JobDetails';
import PdfFormsDashboard from './pages/PdfFormsDashboard';
import PdfFormFill from './pages/PdfFormFill';
import AdminDebug from './pages/AdminDebug';
import AdminUsers from './pages/AdminUsers';
import AdminJobTypes from './pages/AdminJobTypes';
import AdminPdfTemplates from './pages/AdminPdfTemplates';
import AdminPdfTemplateBuilder from './pages/AdminPdfTemplateBuilder';
import AdminPdfTemplatePreview from './pages/AdminPdfTemplatePreview';
import UserProfile from './pages/UserProfile';
import ContactUs from './pages/ContactUs';
import InstructionsManual from './pages/InstructionsManual';
import { refreshTokenRequest } from './utils/api';

const FOOTER_TEXT = `לאור הנדסה ואחזקה תעשייתית בע"מ © ${new Date().getFullYear()} · לואי שלופה`;

// ---------------------------------
// Protected route wrapper
// ---------------------------------
function isTokenValid(token) {
    if (!token) return false;
    try {
        const payload = jwt_decode(token);
        if (!payload?.exp) return true;
        const expiresAt = payload.exp * 1000;
        return Date.now() < expiresAt - 10 * 1000;
    } catch {
        return false;
    }
}

function ProtectedRoute({ children, authReady }) {
    const token = localStorage.getItem('token');
    if (!authReady) {
        return <div className="card">טוען...</div>;
    }
    return token ? children : <Navigate to="/login" replace />;
}

// ---------------------------------
// Admin-only wrapper
// ---------------------------------
function AdminRoute({ children, authReady }) {
    const token = localStorage.getItem('token');
    if (!authReady) {
        return <div className="card">טוען...</div>;
    }
    if (!token) return <Navigate to="/login" replace />;
    let isAdmin = false;
    try {
        const { role } = jwt_decode(token);
        isAdmin = role === 'admin';
    } catch {
        /* invalid token */
    }
    return isAdmin ? children : <Navigate to="/" replace />;
}

// ---------------------------------
// Main App component
// ---------------------------------
export default function App() {
    const [authReady, setAuthReady] = useState(false);

    useEffect(() => {
        let isMounted = true;
        const bootstrap = async () => {
            const token = localStorage.getItem('token');
            if (isTokenValid(token)) {
                if (isMounted) setAuthReady(true);
                return;
            }
            try {
                const data = await refreshTokenRequest();
                if (data?.token) {
                    localStorage.setItem('token', data.token);
                }
            } catch {
                // ignore refresh errors
            } finally {
                if (isMounted) setAuthReady(true);
            }
        };
        bootstrap();
        return () => {
            isMounted = false;
        };
    }, []);

    return (
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <div className="app-shell rtl">
                <Header />
                <main className="app-shell__main">
                    <Routes>
                    <Route path="/" element={<Home />} />
                    <Route
                        path="/instructions-manual"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <InstructionsManual />
                            </ProtectedRoute>
                        }
                    />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<Signup />} />
                    <Route
                        path="/dashboard"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <Dashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/jobs/:id"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <JobDetails />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/forms"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <PdfFormsDashboard />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/pdf-forms/:id"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <PdfFormFill />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/profile"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <UserProfile />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/contact-us"
                        element={
                            <ProtectedRoute authReady={authReady}>
                                <ContactUs />
                            </ProtectedRoute>
                        }
                    />
                    <Route
                        path="/admin/debug"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminDebug />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/users"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminUsers />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/job-types"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminJobTypes />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/pdf-templates"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminPdfTemplates />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/pdf-templates/:id/builder"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminPdfTemplateBuilder />
                            </AdminRoute>
                        }
                    />
                    <Route
                        path="/admin/pdf-templates/:id/preview"
                        element={
                            <AdminRoute authReady={authReady}>
                                <AdminPdfTemplatePreview />
                            </AdminRoute>
                        }
                    />
                    <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </main>
                <footer className="footer">{FOOTER_TEXT}</footer>
            </div>
        </Router>
    );
}
